import { Group, SimpleGrid, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DeviceListResponse } from 'api/actions/device-list/device-list-response';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import { FormProps } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { DeviceSelect } from 'components/selects/DeviceSelect';
import { PriceOfferProviderMultiSelect } from 'components/selects/PriceOfferProviderSelect';
import useLoadingAction from 'hooks/use-loading-action';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { IconCheck } from '@tabler/icons-react';
import { noop } from 'lodash';
import { useMemo, useState } from 'react';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';

export interface PriceOfferAssignFormData {
  deviceId: string;
  assignedTo: string[];
  description: string;
  note: string;
}

/**
 * Form used by assigners to create a price offer.
 */
export default function PriceOfferAssignForm({
  initialValues = {
    deviceId: '',
    assignedTo: [],
    description: '',
    note: '',
  },
  onSubmit = noop,
  readOnly = {},
}: FormProps<PriceOfferAssignFormData> & { readOnly?: Partial<Record<keyof PriceOfferAssignFormData, boolean>> }) {
  const { isTablet } = useResponsiveDesign();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const [devices, setDevices] = useState<DeviceListResponse>([]);

  const form = useForm<PriceOfferAssignFormData>({
    initialValues,
    validate: {
      deviceId: createValidator([required]),
      assignedTo: (value) => required(value)?.message ?? null,
    },
  });

  const selectedDevice = useMemo(
    () => devices.find((device) => String(device.deviceId) === form.values.deviceId),
    [devices, form.values.deviceId]
  );

  const departmentId = selectedDevice?.department?.departmentId;

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText="Vytvoriť"
      primaryIcon={<IconCheck stroke="1.5" height={24} width={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Cenová ponuka">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <DeviceSelect
            label="Zariadenie"
            placeholder="Vyberte zariadenie"
            size="lg"
            showDiscarded={false}
            canAssignPriceOffers
            onReady={(items) => setDevices(items as DeviceListResponse)}
            disabled={readOnly.deviceId}
            {...form.getInputProps('deviceId')}
            onChange={(value) => {
              form.setFieldValue('deviceId', value ?? '');
              form.setFieldValue('assignedTo', []);
            }}
          />
          <PriceOfferProviderMultiSelect
            label={
              <Group spacing={8}>
                Vypracovatelia{' '}
                <QuestionMarkTooltip
                  withArrow
                  multiline
                  width={250}
                  label="Pri výbere viacerých vypracovateľov sa vytvorí viacero cenových ponúk, každá pre jedného vypracovateľa."
                />
              </Group>
            }
            placeholder={departmentId ? 'Vyberte vypracovateľov' : 'Najskôr vyberte zariadenie'}
            size="lg"
            departmentId={departmentId}
            {...form.getInputProps('assignedTo')}
          />
        </SimpleGrid>
        <Textarea
          pt={12}
          label="Popis"
          placeholder="Nepovinný popis pre vypracovateľa"
          minRows={4}
          autosize
          size="lg"
          {...form.getInputProps('description')}
        />
        <Textarea
          pt={12}
          label="Poznámka"
          placeholder="Nepovinná poznámka do záznamu o vytvorení"
          minRows={3}
          autosize
          size="lg"
          {...form.getInputProps('note')}
        />
      </FormInputGroup>
    </FormWrapper>
  );
}
